/**
 * BS 8214 gap-measurement remediation recommender.
 *
 * Inspectors record 12 gap measurements per door leaf:
 *   • Top of door    : top_hinge / top_mid / top_lock
 *   • Hinge side     : hinge_top / hinge_mid / hinge_bot
 *   • Lock side      : lock_top  / lock_mid  / lock_bot
 *   • Bottom (under) : bot_hinge / bot_mid  / bot_lock
 *
 * The acceptable range per BS 8214 is 2–4 mm. When gaps are over-spec the
 * *right* remedy depends on WHICH gaps are over and how they relate.
 *
 * RULES (FD Check guidance, Dave — 27 Jun 2026, updated Iter-163r):
 *
 *   1. UNDER-DOOR (any of bot_hinge / bot_mid / bot_lock) > 4 mm
 *      → "Threshold strip OR automatic drop-down seal"
 *
 *   2. HINGE-SIDE + LOCK-SIDE at the same height summed > 8 mm
 *      Pairs checked: (hinge_top, lock_top), (hinge_mid, lock_mid),
 *                     (hinge_bot, lock_bot)
 *      → "Frame build out or adjustment"
 *
 *   3. Same opposing pair sums ≤ 8 mm BUT one side > 4 mm
 *      → "Centre the door in the frame (ease & re-hang)"
 *
 *   4. TOP-OF-DOOR variation ≤ 2 mm AND any top reading > 4 mm
 *      (gaps even but too large across the whole top)
 *      → "Frame build out or adjustment"
 *
 *   5. TOP-OF-DOOR variation > 2 mm (top head is out of level, whether
 *      or not the biggest reading exceeds 4 mm — because a 2 mm+ tilt
 *      always means the frame head needs correcting)
 *      → "Frame reset required"
 *
 *   6. Iter-108v — Lock-side lean: BOTH lock_top > 4 mm AND lock_bot > 4 mm
 *      The door is pushed away from the latch, usually because the bottom
 *      hinge isn't supporting the leaf. Add: "Pack bottom hinge side" on
 *      top of any other rule that also fires.
 *
 * Iter-163r — User feedback: the previous wording used "Framefit"
 * (a proprietary product brand) which admins didn't want on client-
 * facing reports, and the detail strings were too long. All titles
 * now use the neutral "Frame build out or adjustment" phrasing and
 * details have been tightened to a single line each.
 *
 * These are *suggestions* surfaced inline below the measurement grid so
 * the inspector knows which remedial option to quote for.
 */

export type GapMap = Record<string, string | number | undefined>;

export type GapRecommendation = {
  /** Stable id so the UI can render & dedupe deterministically. */
  id: string;
  /** Short title displayed in the recommendation pill. */
  title: string;
  /** One-line explanation referencing the measurements that triggered it. */
  detail: string;
  /** Coloured severity hint for the UI. */
  severity: 'info' | 'warn' | 'fail';
};

const num = (v: string | number | undefined): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const MAX_PER_SIDE = 4;        // mm per BS 8214 — any single gap > 4 mm = fail
const MAX_OPPOSED_SUM = 8;     // 2 × 4 mm — combined fail for an opposing pair
// Iter-108v — top-gap evenness threshold widened to 2 mm per the RM York
// remedial matrix (spread ≤ 2 mm = "even, lift or Framefit"; spread > 2 mm
// = "frame is tilted, needs a reset").
const TOP_EVENNESS_MM = 2;

/**
 * Compute remedial recommendations from the live gap map. Returns 0+
 * unique recommendations in display order.
 *
 * Iter-108bj — Optional `opts.keyPrefix` lets the caller re-use the same
 * rules on a second leaf of a double door. Pass `keyPrefix='d2_'` +
 * `leafLabel='Second leaf'` to evaluate the second-leaf sub-grid (its
 * 9 measurement keys are d2_top_*, d2_hinge_*, d2_bot_*).
 *
 * Rules that require a `lock_*` gap (2, 3, 6) are automatically skipped
 * when there is no lock-side keyset in the map (the second leaf of a
 * double door hinges on the outside edge and meets the primary leaf on
 * its inside edge — the meeting-stile gap is captured on the primary
 * leaf's `lock_*` keys, so we don't re-evaluate the opposing pairs on
 * the second leaf independently).
 *
 * Iter-108bj — Optional `opts.skipUnderDoor` lets the caller suppress
 * Rule 1 when the door has a working dropdown seal — the seal
 * supersedes any residual under-door gap so no remedy is needed.
 */
export function recommendGapRemedies(
  gaps: GapMap,
  opts: { keyPrefix?: string; leafLabel?: string; skipUnderDoor?: boolean } = {},
): GapRecommendation[] {
  const prefix = opts.keyPrefix || '';
  const leafPrefix = opts.leafLabel ? `${opts.leafLabel}: ` : '';
  const idSuffix = prefix ? `-${prefix.replace(/_$/, '')}` : '';
  // Detect whether a "lock side" keyset exists for this pass. Second
  // leaves of double doors don't have one — they meet the primary leaf
  // instead of the frame, so opposing-pair rules are meaningless.
  const hasLockSide =
    gaps[`${prefix}lock_top`] !== undefined ||
    gaps[`${prefix}lock_mid`] !== undefined ||
    gaps[`${prefix}lock_bot`] !== undefined;

  const out: GapRecommendation[] = [];
  const seen = new Set<string>();
  const push = (r: GapRecommendation) => {
    if (seen.has(r.id)) return;
    seen.add(r.id);
    out.push(r);
  };

  // ── Rule 1: Under-door (bottom) gap > 4 mm ───────────────────────────
  if (!opts.skipUnderDoor) {
    const underEntries = [
      { label: 'hinge side', v: num(gaps[`${prefix}bot_hinge`]) },
      { label: 'middle',     v: num(gaps[`${prefix}bot_mid`])   },
      { label: 'lock side',  v: num(gaps[`${prefix}bot_lock`])  },
    ].filter(e => e.v != null) as { label: string; v: number }[];
    const overs = underEntries.filter(e => e.v > MAX_PER_SIDE);
    if (overs.length > 0) {
      const maxUnder = Math.max(...overs.map(e => e.v));
      const summary = overs.map(e => `${e.label} ${e.v} mm`).join(', ');
      push({
        id: `under-door-over-4${idSuffix}`,
        // Iter-108bk — updated wording per client spec.
        title: `${leafPrefix}Fit dropdown seal / threshold bar, adjust or replace existing seal`,
        detail: `Under-door gap reaches ${maxUnder} mm (max ${MAX_PER_SIDE} mm) — ${summary}. Fit a threshold bar, install an automatic drop-down seal, or adjust / replace the existing seal.`,
        severity: 'fail',
      });
    }
  }

  // ── Rules 2 & 3: HINGE-side vs LOCK-side opposing pairs ──────────────
  //
  // Iter-108bk — Only meaningful when this leaf has a lock-side keyset.
  // BOTH rules now produce ONE deduped recommendation per rule type
  // (previously they emitted per-height, so a door failing at all three
  // heights repeated "Framefit OR frame reset" three times on the
  // report).
  if (hasLockSide) {
    type Pair = { a: string; b: string; height: string };
    const opposed: Pair[] = [
      { a: `${prefix}hinge_top`, b: `${prefix}lock_top`, height: 'top' },
      { a: `${prefix}hinge_mid`, b: `${prefix}lock_mid`, height: 'middle' },
      { a: `${prefix}hinge_bot`, b: `${prefix}lock_bot`, height: 'bottom' },
    ];

    const framefitHits: string[] = [];
    const centreHits: string[] = [];
    for (const p of opposed) {
      const a = num(gaps[p.a]); const b = num(gaps[p.b]);
      if (a == null || b == null) continue;
      const sum = a + b;
      const max = Math.max(a, b);
      if (sum > MAX_OPPOSED_SUM) {
        framefitHits.push(`${p.height}: hinge ${a} mm + lock ${b} mm = ${sum} mm`);
      } else if (max > MAX_PER_SIDE) {
        centreHits.push(`${p.height}: hinge ${a} mm vs lock ${b} mm`);
      }
    }

    if (framefitHits.length > 0) {
      push({
        id: `opposed-sum${idSuffix}`,
        title: `${leafPrefix}Frame build out or adjustment`,
        detail: `Opposing pair over ${MAX_OPPOSED_SUM} mm at ${framefitHits.length === 1 ? 'this height' : `${framefitHits.length} heights`}. Leaf is too narrow for the frame.`,
        severity: 'fail',
      });
    }
    if (centreHits.length > 0) {
      push({
        id: `centre${idSuffix}`,
        title: `${leafPrefix}Centre door in frame (ease & re-hang)`,
        detail: `One side over ${MAX_PER_SIDE} mm at ${centreHits.length === 1 ? 'this height' : `${centreHits.length} heights`} — combined gap within spec, door needs centring.`,
        severity: 'warn',
      });
    }
  }

  // ── Rules 4 & 5: TOP-of-door evenness check ─────────────────────────
  //
  // Applies to any leaf that has top measurements (both primary and
  // second leaves of a double door do). Head-tilted vs door-too-tall
  // are orthogonal failure modes with different remedies.
  const topVals = [
    { key: `${prefix}top_hinge`, label: 'hinge end', v: num(gaps[`${prefix}top_hinge`]) },
    { key: `${prefix}top_mid`,   label: 'centre',   v: num(gaps[`${prefix}top_mid`])   },
    { key: `${prefix}top_lock`,  label: 'lock end', v: num(gaps[`${prefix}top_lock`])  },
  ].filter(t => t.v != null) as { key: string; label: string; v: number }[];

  if (topVals.length >= 2) {
    const values = topVals.map(t => t.v);
    const maxTop = Math.max(...values);
    const minTop = Math.min(...values);
    const spread = maxTop - minTop;

    if (spread > TOP_EVENNESS_MM) {
      push({
        id: `top-uneven-over-2${idSuffix}`,
        title: `${leafPrefix}Frame reset required`,
        detail: `Top-of-door gaps vary by ${spread.toFixed(1)} mm across the head. Frame head is out of level.`,
        severity: 'fail',
      });
    } else if (maxTop > MAX_PER_SIDE) {
      push({
        id: `top-even-over-4${idSuffix}`,
        title: `${leafPrefix}Frame build out or adjustment`,
        detail: `Top-of-door gap is over ${MAX_PER_SIDE} mm but even across the head (variation ${spread.toFixed(1)} mm).`,
        severity: 'fail',
      });
    }
  }

  // ── Rule 6 (Iter-108v): Lock-side lean → pack bottom hinge ──────────
  //
  // Only applies when we have a lock-side keyset (primary leaf).
  if (hasLockSide) {
    const lockTop = num(gaps[`${prefix}lock_top`]);
    const lockBot = num(gaps[`${prefix}lock_bot`]);
    if (lockTop != null && lockBot != null && lockTop > MAX_PER_SIDE && lockBot > MAX_PER_SIDE) {
      push({
        id: `lock-side-lean-pack-bottom-hinge${idSuffix}`,
        title: `${leafPrefix}Pack bottom hinge side`,
        detail: `Lock side is over ${MAX_PER_SIDE} mm at both top (${lockTop} mm) and bottom (${lockBot} mm). Door leaning away from the latch.`,
        severity: 'warn',
      });
    }
  }

  return out;
}
